/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    InspectorLoginParams,
    LoginResponseData,
    MisAuthStatusResponseData,
    MisLoginFormData,
    MisLoginResponseData,
    MisUsesDto,
    UiType,
    UserRole,
} from '../api/auth/authClient.typings';
import { UserAction, UserActionType } from '../actions/userActions';
import { LocaleId } from '../translation/i18n';
import { RouteId, RouteParam } from '../../router/routes';
import {
    CentralInspectorCabinet,
    DirectorCabinet,
    ExpertCabinet,
    InspectorCabinet,
    OperatorCabinet,
    RegionalInspectorCabinet,
    TaxpayerCabinet,
} from '../../accessibility/permitsMock';

export const LOCAL_STORAGE_USER_DATA = 'pks.userData';

export function writeToLocalStorageUserInfo(data: LocalStorageState): void {
    localStorage.setItem(LOCAL_STORAGE_USER_DATA, JSON.stringify(data));
}

export function readFromLocalStorageUserInfo(): LocalStorageState {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_DATA));
}

export interface UserState {
    isLoading: boolean;
    localeId: LocaleId;
    authData: LoginResponseData;
    misAuth: {
        userInfo?: MisLoginFormData;
        startTime?: number;
        verificationCode?: MisLoginResponseData['verificationCode'];
        authStatus?: MisAuthStatusResponseData['authenticationStatus'];
    };
    misUsersList: MisUsesDto[];
    lastMonitoringRoute: {
        routeId: RouteId;
        params?: Record<RouteParam, string>;
    };
    userForRecaptcha: InspectorLoginParams;
}

export type LocalStorageState = Pick<UserState, 'localeId' | 'authData'>;

const initialState: UserState = {
    isLoading: false,
    localeId: LocaleId.Az,
    authData: null,
    misAuth: {},
    misUsersList: null,
    lastMonitoringRoute: null,
    userForRecaptcha: null,
    ...readFromLocalStorageUserInfo(),
};

//TODO: it is permits simulation, remove when real permits will come from gateway
const addPermitsSimulation = (payload: LoginResponseData): LoginResponseData => {
    const authDataWithPermits = { ...payload };
    if (!authDataWithPermits.permits) {
        //console.log('Permits are being simulated...');
        switch (authDataWithPermits.uiType) {
            case UiType.Inspector:
                if (authDataWithPermits.roles.includes(UserRole.Director))
                    authDataWithPermits.permits = DirectorCabinet;
                else if (authDataWithPermits.roles.includes(UserRole.Expert))
                    authDataWithPermits.permits = ExpertCabinet;
                else authDataWithPermits.permits = InspectorCabinet;
                break;
            case UiType.CentralInspector:
                authDataWithPermits.permits = CentralInspectorCabinet;
                break;
            case UiType.RegionInspector:
                authDataWithPermits.permits = RegionalInspectorCabinet;
                break;
            case UiType.Operator:
                authDataWithPermits.permits = OperatorCabinet;
                break;
            case UiType.Taxpayer:
                authDataWithPermits.permits = TaxpayerCabinet;
                break;
        }
    }
    //else console.log('Permits were received with other user data!');
    return authDataWithPermits;
};

function userReducer(state: UserState = initialState, action: UserAction): UserState {
    switch (action.type) {
        case UserActionType.LOGIN_SUCCESS:
            return {
                ...state,
                isLoading: false,
                authData: addPermitsSimulation(action.payload), //TODO: replace by action.payload
                misAuth: {},
            };
        case UserActionType.LOGIN_LOADING:
            return {
                ...state,
                isLoading: true,
            };

        case UserActionType.LOGOUT_USER:
            return {
                ...state,
                authData: null,
                misUsersList: null,
                lastMonitoringRoute: null,
            };

        case UserActionType.CHANGE_LOCALE:
            return {
                ...state,
                localeId: action.payload,
            };

        case UserActionType.LOGIN_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case UserActionType.SET_MIS_NOTIFICATION:
            return {
                ...state,
                misAuth: {},
                isLoading: false,
            };

        case UserActionType.MIS_SEND_USER_INFO:
            return {
                ...state,
                misAuth: {
                    ...state.misAuth,
                    //TODO: change this logic when real asan login will work
                    userInfo: {
                        personalID: action.payload.formData.personalID,
                        phoneNumber: action.payload.formData.phoneNumber,
                    },
                },
                misUsersList: null,
                isLoading: true,
            };

        case UserActionType.MIS_USER_INFO_SUCCESS:
            return {
                ...state,
                misAuth: {
                    ...state.misAuth,
                    verificationCode: action.payload.verificationCode,
                    startTime: Date.now(),
                },
                isLoading: false,
            };

        case UserActionType.MIS_AUTH_STATUS_LOAD:
            const authStatus = action.payload.authenticationStatus;

            if (authStatus === 'USER_AUTHENTICATED') {
                return {
                    ...state,
                    misAuth: {},
                    misUsersList: action.payload.users,
                };
            }

            return {
                ...state,
            };

        case UserActionType.MIS_RESET:
            return {
                ...state,
                misUsersList: null,
                misAuth: {},
            };

        case UserActionType.MIS_AUTH_COMPLETE_START:
            return {
                ...state,
                isLoading: true,
            };

        case UserActionType.MIS_AUTH_COMPLETE_SUCCESS:
            return {
                ...state,
                authData: addPermitsSimulation(action.payload), //TODO: replace by action.payload
                isLoading: false,
            };

        case UserActionType.SAVE_LAST_MONITORING_ROUTE:
            return {
                ...state,
                lastMonitoringRoute: action.payload,
            };

        case UserActionType.VALIDATE_USER_ACTION:
            return {
                ...state,
                userForRecaptcha: action.payload,
            };

        case UserActionType.COMPLETE_VALIDATE_USER_ACTION:
            return {
                ...state,
                userForRecaptcha: null,
            };

        default:
            return state;
    }
}

export default userReducer;
